/**
 * useOfflineStorage composable
 * 
 * Vue 3 Composition API composable for offline storage operations.
 * Wraps UnifiedDBManagerV2 for reactive integration with Pinia store.
 * 
 * @author AI Assistant
 * @version 2.0.0
 */

import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useMessagesApi } from './useMessagesApi';
import { logger } from '../utils';

// UnifiedDBv2 Interface
interface UnifiedDBv2 {
  init(): Promise<void>;
  saveMessage(message: any, options?: { storeMedia?: boolean }): Promise<any>;
  getMessages(conversationId: string, options?: { includeOffline?: boolean; limit?: number; offset?: number }): Promise<any[]>;
  getOfflineMessages(): Promise<any[]>;
  markMessageSynced(messageId: string, serverId?: string): Promise<void>;
  saveMediaBlob(messageId: string, attachment: any): Promise<any>;
  getMediaBlob(messageId: string, attachmentId: string): Promise<{ blob: Blob; id: string } | null>;
  saveDraft(conversationId: string, draft: any): Promise<void>;
  getDraft(conversationId: string): Promise<any>;
  deleteDraft(conversationId: string): Promise<void>;
  queueOperation(type: string, data: any, options?: any): Promise<any>;
  getPendingOperations(options?: { status?: string }): Promise<any[]>;
  syncWithServer(api: any): Promise<{ success: boolean; stats: any }>;
  requestBackgroundSync(): Promise<void>;
  registerBackgroundSync(): Promise<boolean>;
  resolveMessageConflict(local: any, server: any): Promise<any>;
  cleanupOldData(): Promise<number>;
  checkStorageQuota(): Promise<{ usage: number; quota: number; percentUsed: number }>;
  count(storeName: string): Promise<number>;
  get(storeName: string, key: any): Promise<any>;
  delete(storeName: string, key: any): Promise<void>;
  put(storeName: string, value: any): Promise<void>;
}

// Types
export interface OfflineMessage {
  id: string;
  text: string;
  conversationId: string;
  contactId?: string;
  from_user_id: string | number;
  timestamp: number;
  status: 'pending' | 'failed' | 'delivered' | 'read';
  type: 'sent' | 'received';
  synced: boolean;
  _offline: boolean;
  _storedAt?: number;
  attachments?: any[];
  // Properties for NormalisedMessage compatibility
  ts?: number;
  is_read?: boolean;
  user?: { id: string | number; avatar?: string | null };
  // Backend soft deletion flags
  is_deleted_by_sender?: boolean;
  is_deleted_by_receiver?: boolean;
  // User isolation for shared-device security
  ownerUserId?: string | number;
}

export interface PendingOperation {
  id: string;
  type: 'send_message' | 'edit_message' | 'delete_message' | 'toggle_reaction';
  data: any;
  userId?: string | number | null;
  timestamp: number;
  priority: 'high' | 'normal' | 'low';
  status: 'pending' | 'completed' | 'failed';
  retries: number;
  lastError?: string;
}

export interface StorageStats {
  messages: number;
  mediaBlobs: number;
  pendingOperations: number;
  conversations: number;
  drafts: number;
  usage: number;
  quota: number;
  percentUsed: number;
}

export function useOfflineStorage() {
  // Reactive state
  const isInitialized = ref(false);
  const isSyncing = ref(false);
  const pendingCount = ref(0);
  const storageStats = ref<StorageStats | null>(null);
  const lastSyncAt = ref<number | null>(null);
  const syncError = ref<string | null>(null);
  
  // DB reference
  const db = computed(() => window.unifiedDBv2 as unknown as UnifiedDBv2);
  const api = useMessagesApi();

  // ==================== INITIALIZATION ====================

  const init = async () => {
    if (isInitialized.value) return true;
    
    try {
      if (!db.value) {
        logger.error('[useOfflineStorage] UnifiedDBv2 not available');
        return false;
      }
      
      await db.value.init();
      isInitialized.value = true;
      
      // Load initial stats
      await refreshStats();
      
      // Setup event listeners
      setupEventListeners();
      
      logger.log('[useOfflineStorage] Initialized');
      return true;
    } catch (error) {
      logger.error('[useOfflineStorage] Init failed:', error);
      return false;
    }
  };

  const setupEventListeners = () => {
    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Listen for sync requests
    window.addEventListener('db-sync-requested', handleSyncRequest);
    
    // Listen for storage pressure
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      // Check periodically
      setInterval(refreshStats, 60000);
    }
  };

  const cleanup = () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('db-sync-requested', handleSyncRequest);
  };

  onUnmounted(cleanup);

  // ==================== MESSAGE OPERATIONS ====================

  /**
   * Save message to offline storage
   */
  const saveOfflineMessage = async (message: Partial<OfflineMessage>): Promise<OfflineMessage> => {
    await init();
    
    const data: OfflineMessage = {
      id: message.id || generateId(),
      text: message.text || '',
      conversationId: message.conversationId || message.contactId || '',
      contactId: message.contactId,
      from_user_id: message.from_user_id || getCurrentUserId(),
      timestamp: message.timestamp || Math.floor(Date.now() / 1000),
      status: 'pending',
      type: 'sent',
      synced: false,
      _offline: true,
      attachments: message.attachments || []
    };

    await db.value!.saveMessage(data, { storeMedia: false });
    await refreshStats();
    
    logger.log('[useOfflineStorage] Message saved offline:', data.id);
    return data;
  };

  /**
   * Get offline messages for a conversation
   */
  const getOfflineMessages = async (conversationId: string): Promise<OfflineMessage[]> => {
    await init();
    // Load all messages for offline viewing (no pagination limit)
    return await db.value!.getMessages(conversationId, { includeOffline: true, limit: 10000 });
  };

  /**
   * Get all pending offline messages
   */
  const getAllPendingMessages = async (): Promise<OfflineMessage[]> => {
    await init();
    return await db.value!.getOfflineMessages();
  };

  /**
   * Restore pending messages to the store
   */
  const restorePendingMessages = async (chatId?: string): Promise<OfflineMessage[]> => {
    await init();
    
    const messages = await db.value!.getOfflineMessages();
    const filtered = chatId 
      ? messages.filter((m: any) => String(m.conversationId) === String(chatId))
      : messages;

    logger.log('[useOfflineStorage] Restored', filtered.length, 'pending messages');
    return filtered;
  };

  /**
   * Mark message as synced
   */
  const markMessageSynced = async (messageId: string, serverId?: string): Promise<void> => {
    await init();
    await db.value!.markMessageSynced(messageId, serverId);
    await refreshStats();
  };

  /**
   * Delete message from storage
   */
  const deleteMessage = async (messageId: string): Promise<void> => {
    await init();
    await db.value!.delete('messages', messageId);
    await refreshStats();
  };

  // ==================== MEDIA OPERATIONS ====================

  /**
   * Save media blob for offline use
   */
  const saveMediaOffline = async (messageId: string, file: File): Promise<any> => {
    await init();
    
    const attachment = {
      id: `file_${Date.now()}`,
      name: file.name,
      type: file.type,
      blob: file,
      size: file.size
    };

    return await db.value!.saveMediaBlob(messageId, attachment);
  };

  /**
   * Get media blob from cache
   */
  const getMediaBlob = async (messageId: string, attachmentId: string): Promise<{ blob: Blob; id: string } | null> => {
    await init();
    return await db.value!.getMediaBlob(messageId, attachmentId);
  };

  /**
   * Check if media is available offline
   */
  const isMediaAvailableOffline = async (messageId: string, attachmentId: string): Promise<boolean> => {
    await init();
    const metadata = await db.value!.get('media_blobs', `media_${messageId}_${attachmentId}`);
    return !!metadata;
  };

  // ==================== DRAFT OPERATIONS ====================

  /**
   * Save message draft
   */
  const saveDraft = async (conversationId: string, draft: { text: string; attachments?: any[]; replyTo?: any }): Promise<void> => {
    await init();
    await db.value!.saveDraft(conversationId, draft);
    logger.log('[useOfflineStorage] Draft saved for:', conversationId);
  };

  /**
   * Get message draft
   */
  const getDraft = async (conversationId: string): Promise<{ text: string; attachments: any[]; replyTo: any } | null> => {
    await init();
    return await db.value!.getDraft(conversationId);
  };

  /**
   * Delete draft
   */
  const deleteDraft = async (conversationId: string): Promise<void> => {
    await init();
    await db.value!.deleteDraft(conversationId);
  };

  // ==================== PENDING OPERATIONS ====================

  /**
   * Queue operation for sync
   */
  const queueOperation = async (
    type: PendingOperation['type'], 
    data: any, 
    options: { priority?: 'high' | 'normal' | 'low'; maxRetries?: number } = {}
  ): Promise<PendingOperation> => {
    await init();
    
    const operation = await db.value!.queueOperation(type, data, options);
    await refreshStats();
    
    // Request background sync (intentionally fire-and-forget)
    void db.value!.requestBackgroundSync();
    
    return operation;
  };

  /**
   * Get pending operations count
   */
  const getPendingOperationsCount = async (): Promise<number> => {
    await init();
    const ops = await db.value!.getPendingOperations({ status: 'pending' });
    return ops.length;
  };

  /**
   * Get all pending operations
   */
  const getPendingOperations = async (): Promise<PendingOperation[]> => {
    await init();
    return await db.value!.getPendingOperations({ status: 'pending' });
  };

  // ==================== SYNC OPERATIONS ====================

  /**
   * Sync all pending data with server
   */
  const syncWithServer = async (): Promise<{ success: boolean; stats: any }> => {
    if (isSyncing.value) {
      return { success: false, stats: { skipped: 'already syncing' } };
    }

    if (!navigator.onLine) {
      return { success: false, stats: { error: 'offline' } };
    }

    isSyncing.value = true;
    syncError.value = null;

    try {
      await init();
      
      const result = await db.value!.syncWithServer(api);
      
      lastSyncAt.value = Date.now();
      await refreshStats();
      
      logger.log('[useOfflineStorage] Sync completed:', result);
      return result;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      syncError.value = errorMsg;
      logger.error('[useOfflineStorage] Sync failed:', error);
      return { success: false, stats: { error: errorMsg } };
    } finally {
      isSyncing.value = false;
    }
  };

  /**
   * Handle online event
   */
  const handleOnline = async () => {
    logger.log('[useOfflineStorage] Online detected, scheduling sync');
    
    // Delay sync to ensure connection is stable
    setTimeout(async () => {
      if (navigator.onLine) {
        await syncWithServer();
      }
    }, 1500);
  };

  /**
   * Handle offline event
   */
  const handleOffline = () => {
    logger.log('[useOfflineStorage] Offline detected');
  };

  /**
   * Handle sync request event
   */
  const handleSyncRequest = async () => {
    logger.log('[useOfflineStorage] Sync requested');
    await syncWithServer();
  };

  // ==================== STORAGE MANAGEMENT ====================

  /**
   * Refresh storage statistics
   */
  const refreshStats = async (): Promise<void> => {
    await init();
    
    try {
      const [messages, mediaBlobs, pendingOps, conversations, drafts, quota] = await Promise.all([
        db.value!.count('messages'),
        db.value!.count('media_blobs'),
        db.value!.count('pending_operations'),
        db.value!.count('conversations'),
        db.value!.count('drafts'),
        db.value!.checkStorageQuota()
      ]);

      storageStats.value = {
        messages,
        mediaBlobs,
        pendingOperations: pendingOps,
        conversations,
        drafts,
        usage: quota?.usage || 0,
        quota: quota?.quota || Infinity,
        percentUsed: quota?.percentUsed || 0
      };

      pendingCount.value = pendingOps;
    } catch (error) {
      logger.error('[useOfflineStorage] Failed to refresh stats:', error);
    }
  };

  /**
   * Get storage stats (reactive)
   */
  const getStorageStats = async (): Promise<StorageStats> => {
    await refreshStats();
    return storageStats.value!;
  };

  /**
   * Clean up old data
   */
  const cleanupOldData = async (): Promise<number> => {
    await init();
    const cleaned = await db.value!.cleanupOldData();
    await refreshStats();
    return cleaned;
  };

  /**
   * Check if storage is under pressure
   */
  const isStorageWarning = computed(() => {
    return (storageStats.value?.percentUsed || 0) > 0.8;
  });

  const isStorageCritical = computed(() => {
    return (storageStats.value?.percentUsed || 0) > 0.9;
  });

  // ==================== CONFLICT RESOLUTION ====================

  /**
   * Resolve conflict between local and server message
   */
  const resolveMessageConflict = async (localMessage: any, serverMessage: any): Promise<any> => {
    await init();
    return await db.value!.resolveMessageConflict(localMessage, serverMessage);
  };

  // ==================== BACKGROUND SYNC ====================

  /**
   * Register for background sync
   */
  const registerBackgroundSync = async (): Promise<boolean> => {
    await init();
    return await db.value!.registerBackgroundSync();
  };

  /**
   * Request immediate sync
   */
  const requestSync = async (): Promise<void> => {
    await init();
    await db.value!.requestBackgroundSync();
  };

  // ==================== UTILITIES ====================

  const generateId = (): string => {
    return 'msg_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 11);
  };

  const getCurrentUserId = (): string => {
    return (window as any).__currentUserId || 
           (window as any).currentUser?.id || 
           'unknown';
  };

  const formatStorageSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Auto-init on mount (intentionally fire-and-forget)
  onMounted(() => {
    void init();
  });

  // Return public API
  return {
    // State
    isInitialized,
    isSyncing,
    pendingCount,
    storageStats,
    lastSyncAt,
    syncError,
    isStorageWarning,
    isStorageCritical,

    // Methods
    init,
    saveOfflineMessage,
    getOfflineMessages,
    getAllPendingMessages,
    restorePendingMessages,
    markMessageSynced,
    deleteMessage,
    
    // Media
    saveMediaOffline,
    getMediaBlob,
    isMediaAvailableOffline,
    
    // Drafts
    saveDraft,
    getDraft,
    deleteDraft,
    
    // Operations
    queueOperation,
    getPendingOperationsCount,
    getPendingOperations,
    
    // Sync
    syncWithServer,
    registerBackgroundSync,
    requestSync,
    
    // Storage
    refreshStats,
    getStorageStats,
    cleanup: cleanupOldData,
    formatStorageSize,
    
    // Conflicts
    resolveMessageConflict,

    // Cleanup
    dispose: cleanup
  };
}
